import { parseISO, format, isValid } from "date-fns";

/** Типы по умолчанию в запросе (совпадают с сервером). */
export const DEFAULT_DANGEROUS_EVENT_TYPES = [
  "deletedPrintedItems",
  "orderReturned",
  "returnProductsChequePrint",
  "demandBalanceCheckFailed",
  "emergencyPaymentCancel",
];

/** Дополнительные типы в фильтре (актуальные id из iiko). */
export const SELECTABLE_DANGEROUS_EVENT_TYPES = [
  ...DEFAULT_DANGEROUS_EVENT_TYPES,
  "deletedNewItems",
  "emergencyItemsDeletion",
  "orderDiscounted",
  "orderDiscountDeleted",
  "deletedExternalPayment",
  "payInMade",
  "payOutMade",
];

const IIKO_EVENT_TYPE_RU = {
  deletedPrintedItems: "Удаление отпечатанных позиций заказа",
  deletedNewItems: "Удаление неотпечатанных позиций заказа",
  emergencyItemsDeletion: "Аварийное удаление позиций заказа",
  orderReturned: "Сторнирование чека",
  returnProductsChequePrint: "Возврат продажи",
  demandBalanceCheckFailed: "Расхождение при контрольном пересчете",
  emergencyPaymentCancel: "Аварийное удаление оплаты",
  orderDiscounted: "Добавление скидки/надбавки",
  orderDiscountDeleted: "Удаление скидки/надбавки",
  deletedExternalPayment: "Удаление внешней оплаты",
  payInMade: "Внесение наличных",
  payOutMade: "Изъятие наличных",
  orderDeleted: "Удаление заказа",
  cashAutoCorrection: "Обнаружено расхождение суммы в кассе",
  emergencyMoveToPrecheque: "Аварийный перевод заказа в пречек",
  orderPaid: "Оплата заказа",
  orderCancelPrecheque: "Отмена пречека",
  orderPrechequed: "Распечатка пречека",
  orderMoved: "Перенос заказа",
  orderClosedAtWaiterExpense: "Принудительное закрытие заказа",
  orderDiscountChanged: "Изменение скидки",
  orderExternalPaymentDeleted: "Удаление внешней оплаты",
  orderPaymentDeleted: "Удаление оплаты",
  orderStorned: "Сторнирование чека",
  orderStorno: "Сторнирование чека",
  orderDishDeleted: "Удаление неотпечатанных позиций заказа",
  deletedNonPrintedItems: "Удаление неотпечатанных позиций заказа",
  orderDiscountAdded: "Добавление скидки/надбавки",
  dishReturn: "Возврат продажи",
  payInOutDeleted: "Удаление внесения/изъятия",
  orderDishAmountDecreased: "Изменение количества гостей заказа",
};

export const EVENT_ATTRIBUTE_LABELS = {
  orderNum: "Заказ №",
  sum: "Сумма",
  orderSumAfterDiscount: "Сумма со скидкой",
  waiter: "Официант",
  newWaiter: "Новый официант",
  user: "Пользователь",
  auth: "Доп. авторизация",
  employee: "Сотрудник",
  tableNum: "Стол",
  openTime: "Открытие заказа",
  paymentType: "Тип оплаты",
  operationWithWriteoff: "Со списанием",
  comment: "Комментарий",
  dishes: "Позиции",
  numGuests: "Гостей",
  restaurantGroup: "Группа",
  addedPayments: "Оплаты",
  isBanquet: "Банкет",
  departmentId: "Подразделение",
};

/** Колонки таблицы опасных операций (после даты и типа). */
export const DANGEROUS_EVENT_ROW_FIELDS = [
  { key: "dateLabel", label: "Дата", fixed: true },
  { key: "typeLabel", label: "Тип", fixed: true },
  { key: "orderNum", label: EVENT_ATTRIBUTE_LABELS.orderNum, attr: "orderNum" },
  { key: "sum", label: EVENT_ATTRIBUTE_LABELS.sum, attr: "sum" },
  { key: "orderSumAfterDiscount", label: EVENT_ATTRIBUTE_LABELS.orderSumAfterDiscount, attr: "orderSumAfterDiscount" },
  { key: "waiter", label: EVENT_ATTRIBUTE_LABELS.waiter, attr: "waiter" },
  { key: "newWaiter", label: EVENT_ATTRIBUTE_LABELS.newWaiter, attr: "newWaiter" },
  { key: "user", label: EVENT_ATTRIBUTE_LABELS.user, attrs: ["user", "auth", "employee"] },
  { key: "tableNum", label: EVENT_ATTRIBUTE_LABELS.tableNum, attr: "tableNum" },
  { key: "openTime", label: EVENT_ATTRIBUTE_LABELS.openTime, attr: "openTime", format: "datetime" },
  { key: "paymentType", label: EVENT_ATTRIBUTE_LABELS.paymentType, attr: "paymentType" },
  { key: "operationWithWriteoff", label: EVENT_ATTRIBUTE_LABELS.operationWithWriteoff, attr: "operationWithWriteoff", format: "bool" },
  { key: "numGuests", label: EVENT_ATTRIBUTE_LABELS.numGuests, attr: "numGuests" },
  { key: "restaurantGroup", label: EVENT_ATTRIBUTE_LABELS.restaurantGroup, attr: "restaurantGroup" },
  { key: "addedPayments", label: EVENT_ATTRIBUTE_LABELS.addedPayments, attr: "addedPayments" },
  { key: "isBanquet", label: EVENT_ATTRIBUTE_LABELS.isBanquet, attr: "isBanquet", format: "bool" },
  { key: "departmentId", label: EVENT_ATTRIBUTE_LABELS.departmentId },
  { key: "comment", label: EVENT_ATTRIBUTE_LABELS.comment, attr: "comment" },
  { key: "dishes", label: EVENT_ATTRIBUTE_LABELS.dishes, attr: "dishes" },
];

function normalizeTypeKey(s) {
  return String(s).toLowerCase().replace(/[\s_.-]+/g, "");
}

const TYPE_RU_NORMALIZED = Object.fromEntries(
  Object.entries(IIKO_EVENT_TYPE_RU).map(([k, v]) => [normalizeTypeKey(k), v]),
);

export function formatDangerousEventType(ev) {
  if (!ev) return "—";
  if (ev.typeName && typeof ev.typeName === "string") return ev.typeName;
  if (ev.typeTitle && typeof ev.typeTitle === "string") return ev.typeTitle;

  const type = ev.type;
  if (!type) return "—";

  if (IIKO_EVENT_TYPE_RU[type]) return IIKO_EVENT_TYPE_RU[type];
  const mapped = TYPE_RU_NORMALIZED[normalizeTypeKey(type)];
  if (mapped) return mapped;

  return String(type)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
}

export function attrsToMap(attrs) {
  const m = {};
  for (const a of attrs || []) {
    m[a.name] = a.displayName != null && a.displayName !== "" ? a.displayName : a.value;
  }
  return m;
}

function formatBoolValue(value) {
  if (value == null || value === "" || value === "—") return "—";
  const v = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "да"].includes(v)) return "Да";
  if (["false", "0", "no", "нет"].includes(v)) return "Нет";
  return String(value);
}

function formatDateTimeValue(value) {
  if (value == null || value === "" || value === "—") return "—";
  const raw = String(value).trim();
  let d = parseISO(raw);
  if (!isValid(d)) d = parseISO(raw.replace(" ", "T"));
  if (!isValid(d)) {
    const fallback = new Date(raw);
    if (isValid(fallback)) d = fallback;
  }
  return isValid(d) ? format(d, "dd.MM.yyyy HH:mm:ss") : raw;
}

function pickAttrValue(am, field) {
  if (field.attrs) {
    for (const id of field.attrs) {
      const v = am[id];
      if (v != null && String(v).trim() !== "" && v !== "—") return v;
    }
    return "—";
  }
  if (field.attr) {
    const v = am[field.attr];
    return v != null && String(v).trim() !== "" ? v : "—";
  }
  return "—";
}

function formatFieldValue(value, formatType) {
  if (value === "—") return value;
  if (formatType === "bool") return formatBoolValue(value);
  if (formatType === "datetime") return formatDateTimeValue(value);
  return String(value);
}

export function buildDangerousEventRow(ev, idx, formatEventDateTime) {
  const am = attrsToMap(ev.attributes);
  const row = {
    key: `${ev.id}-${idx}`,
    ev,
    am,
    dateLabel: formatEventDateTime(ev.date),
    typeLabel: formatDangerousEventType(ev),
  };

  for (const field of DANGEROUS_EVENT_ROW_FIELDS) {
    if (field.fixed) continue;
    if (field.key === "departmentId") {
      row.departmentId = ev.departmentId ?? am.departmentId ?? "—";
      continue;
    }
    row[field.key] = formatFieldValue(pickAttrValue(am, field), field.format);
  }

  return row;
}

export function resolveEventUser(am) {
  return am.user ?? am.auth ?? am.employee ?? "—";
}
